import React from "react";
import { leadHeat, memberRisk, trialConversionLikelihood } from "../lib/scoring";

const HEAT_STYLE = {
  hot: { icon: "🔥", classes: "bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200 border-rose-200 dark:border-rose-800/40" },
  warm: { icon: "🟡", classes: "bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200 border-amber-200 dark:border-amber-800/40" },
  cold: { icon: "❄️", classes: "bg-sky-100 text-sky-800 dark:bg-sky-950/40 dark:text-sky-200 border-sky-200 dark:border-sky-800/40" },
  won: { icon: "✓", classes: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800/40" },
};

const RISK_STYLE = {
  low: { classes: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800/40" },
  medium: { classes: "bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200 border-amber-200 dark:border-amber-800/40" },
  high: { classes: "bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200 border-rose-200 dark:border-rose-800/40" },
};

const LIKELIHOOD_STYLE = {
  high: { classes: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800/40" },
  medium: { classes: "bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200 border-amber-200 dark:border-amber-800/40" },
  low: { classes: "bg-muted/60 text-muted-foreground border-border" },
  won: { classes: "bg-emerald-200 text-emerald-900 dark:bg-emerald-900/50 dark:text-emerald-100 border-emerald-300" },
};

const SIZES = {
  sm: "text-[10px] px-1.5 py-0.5 gap-0.5",
  md: "text-[11px] px-2 py-0.5 gap-1",
};

export function LeadHeatBadge({ lead, size = "md", className = "" }) {
  const { tier, label, score, reason } = leadHeat(lead);
  const style = HEAT_STYLE[tier] || HEAT_STYLE.cold;
  return (
    <span
      className={`inline-flex items-center font-semibold rounded-full border ${style.classes} ${SIZES[size]} ${className}`}
      title={`${label} (score ${score})${reason ? " · " + reason : ""}`}
      data-testid={`lead-heat-${tier}`}
    >
      <span aria-hidden>{style.icon}</span>
      <span>{label}</span>
    </span>
  );
}

export function MemberRiskBadge({ member, size = "md", className = "" }) {
  const { tier, label, reason } = memberRisk(member);
  if (tier === "low") return null; // healthy members get no badge - keeps the UI clean
  const style = RISK_STYLE[tier];
  return (
    <span
      className={`inline-flex items-center font-semibold rounded-full border ${style.classes} ${SIZES[size]} ${className}`}
      title={reason || label}
      data-testid={`member-risk-${tier}`}
    >
      {label}
    </span>
  );
}

export function ConversionLikelihoodBadge({ lead, size = "md", className = "" }) {
  const { tier, label, reason } = trialConversionLikelihood(lead);
  const style = LIKELIHOOD_STYLE[tier] || LIKELIHOOD_STYLE.low;
  return (
    <span
      className={`inline-flex items-center font-semibold rounded-full border ${style.classes} ${SIZES[size]} ${className}`}
      title={reason || `${label} likelihood`}
      data-testid={`conversion-likelihood-${tier}`}
    >
      {label} chance
    </span>
  );
}
